import { useEffect, useRef, useState, useCallback } from "react";
import { errorNotification } from "../../utils/Notifications";

export function useVoiceInput(
  setText: (text: React.SetStateAction<string>) => void,
) {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const transcriptionUrl = import.meta.env.VITE_API_STT_URL;

  const sendToSTT = useCallback(
    async (blob: Blob) => {
      setIsProcessing(true);
      try {
        const filename = `recording-${Date.now()}.${blob.type.split("/")[1]}`;
        const file = new File([blob], filename, { type: blob.type });
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch(`${transcriptionUrl}/transcribeSpeech`, {
          method: "POST",
          body: formData,
        });

        if (!res.ok) {
          throw new Error(`Transcription server error: ${res.status}`);
        }

        const data: { text: string } = await res.json();
        setText((prev) => {
          if (!prev) return data.text;
          return prev + (prev.endsWith(" ") ? "" : " ") + data.text;
        });
      } catch (err) {
        console.error(err);
        errorNotification("Unable to process audio input.");
      } finally {
        setIsProcessing(false);
      }
    },
    [setText, transcriptionUrl],
  );

  const handleStartRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        await sendToSTT(blob);
        stream.getTracks().forEach((t) => t.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error(err);
      errorNotification(
        "Unable to access microphone. Please check permissions.",
      );
    }
  }, [sendToSTT]);

  const handleStopRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      mr.stop();
    }
    setIsRecording(false);
  }, []);

  useEffect(() => {
    return () => {
      // this runs on unmount
      handleStopRecording();
    };
  }, [handleStopRecording]);

  return {
    isConfigured: !!transcriptionUrl,
    isRecording,
    isProcessing,
    handleStartRecording,
    handleStopRecording,
  };
}
